<template>
    <div v-if="isOpen" class="modal" :style="{ display: 'block' }" @click.self="closeModal">
        <div class="modal-content-container">
            <div class="modal-content">
                <div v-if="selectedMethod === PAYMENT_METHOD_CASH" class="total-to-pay">
                    <p class="label">{{ orderStatus == STATUS_PENDING ? t('totalAmount') : t('returnChange') }}</p>
                    <p class="amount">฿{{ orderStatus == STATUS_PENDING ? newTotalAmount.toFixed(2) :
                        getChange().toFixed(2) }}</p>
                </div>
                <div v-else class="total-to-pay">
                    <p class="label">{{ t('totalAmount') }}</p>
                    <p class="amount">฿{{ newTotalAmount.toFixed(2) }}</p>
                </div>

                <div v-if="orderStatus == STATUS_PROCESSING" class="order-messages-section">
                    <h2 class="order-success">{{ orderMessages.order.message }}</h2>
                    <h4 class="success-message" v-if="selectedMethod === PAYMENT_METHOD_CASH">{{
                        orderMessages.payment.message }}</h4>
                </div>

                <!-- Payment Section -->
                <div v-if="orderStatus == STATUS_PENDING" class="payment-section">
                    <template v-if="selectedMethod === PAYMENT_METHOD_PROMPTPAY">
                        <p class="payment-instruction">
                            {{ t('promptpayInstruction') }}
                        </p>
                        <div class="qr-code-placeholder">
                            <div v-if="!promptPayValue">{{ t('generatingQrCode') }}</div>
                            <VueQrcode v-else :value="promptPayValue" :size="QR_CODE_SIZE" />
                        </div>
                        <div class="cash-input-container">
                            <div class="cash-input">
                                <label>{{ t('discount') }}</label>
                                <input type="number" min="0" step="0.01" v-model="cashDiscount"
                                    :placeholder="t('enterDiscount')" @input="calculateDue" />
                            </div>
                            <p v-if="errorMessages['discount']" class="error-message">{{ errorMessages['discount']
                                }}
                            </p>
                        </div>
                        <p v-if="isProcessing">{{ t('waitingForPayment', { countdown }) }}</p>
                    </template>

                    <template v-if="selectedMethod === PAYMENT_METHOD_CASH">
                        <p class="payment-instruction">
                            {{ t('cashInstruction') }}
                        </p>
                        <div class="cash-input-container">
                            <div class="cash-input">
                                <label>{{ t('discount') }}</label>
                                <input type="number" min="0" step="0.01" v-model="cashDiscount"
                                    :placeholder="t('enterDiscount')" @input="calculateDue" />
                            </div>
                            <p v-if="errorMessages['discount']" class="error-message">{{ errorMessages['discount'] }}
                            </p>
                            <div class="cash-input">
                                <label>{{ t('cashReceived') }}</label>
                                <input type="number" min="0" step="0.01" v-model="cashReceived"
                                    :placeholder="t('enterAmount')" @input="calculateDue" />
                            </div>
                            <p v-if="errorMessages['receivedCash']" class="error-message">{{
                                errorMessages['receivedCash'] }}
                            </p>
                            <div class="cash-input">
                                <label>{{ t('change') }}</label>
                                <input type="number" min="0" step="0.01" v-model="change" placeholder="" readonly />
                            </div>
                            <p v-if="errorMessages['change']" class="error-message">{{ errorMessages['change'] }}
                            </p>
                        </div>
                    </template>
                    <p v-if="errorMessages['process']" class="error-message">{{ errorMessages['process'] }}</p>
                    <p
                        :class="{ 'success-message': transactionStatus === STATUS_COMPLETED, 'error-message': transactionStatus === 'failed' }">
                        {{ transactionMessage }}
                    </p>
                </div>

                <!-- Actions -->
                <div class="modal-actions-container">
                    <template v-if="orderStatus == STATUS_PROCESSING">
                        <button @click="closeModal" class="btn btn-charcoal">{{ t('close') }}</button>
                        <PrintInReceiptButton />
                    </template>

                    <template v-else>
                        <template v-if="transactionStatus">
                            <button @click="closeModal" class="btn btn-charcoal">{{ t('close') }}</button>
                            <button v-if="transactionStatus === STATUS_FAILED" @click="confirmPayment()"
                                class="btn btn-blue"> {{ t('retry') }}
                            </button>
                            <PrintInReceiptButton v-if="transactionStatus === STATUS_COMPLETED" />
                        </template>

                        <template v-else>
                            <button @click="closeModal" class="btn btn-charcoal">{{ t('cancel') }}</button>
                            <button v-if="canCreateOrder" class="btn btn-blue"
                                :disabled="isProcessing || !canMakePayment" @click="confirmPayment">
                                {{ isProcessing ? t('processing') : t('confirmPayment') }}
                            </button>
                        </template>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, onBeforeUnmount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { debounce } from 'lodash';
import eventBus from '@/eventBus';
import { useOrderStore } from '@/stores/order';
import { usePaymentSettings } from '@/stores/paymentSettings';
import { useBroadcastComposable } from '@/composables/useBroadcastComposable';
import { useOrder } from "@/composables/useOrder";
import PrintInReceiptButton from '@/components/order/buttons/printInReceiptButton.vue';
import VueQrcode from '@chenfengyuan/vue-qrcode';
import {
    STATUS_PENDING,
    STATUS_PROCESSING,
    STATUS_COMPLETED,
    STATUS_FAILED,
    PAYMENT_METHOD_CASH,
    PAYMENT_METHOD_PROMPTPAY,
    DISCOUNT_TYPE_FIXED
} from "@/constants/order";

const props = defineProps({
    isOpen: { type: Boolean, required: true }
});

const emit = defineEmits(['update:isOpen']);

const { t } = useI18n();

const { broadcastOrderToCds } = useBroadcastComposable();
const { canCreateOrder, createOrder } = useOrder();
const { setPaymentReferenceId } = usePaymentSettings();
const { getChange, getCurrentTable, getCustomerName, getDiscount, getMessages, getOrderItems, getOrderStatus, getOrderType, getPaymentMethod, getTotalAmount, updateCashReceived, updateChange, updateDiscount, updateMessages, updateOrderId, updateOrderStatus, updateTransactionStatus } = useOrderStore();

/**
 * Computed property for order-related messages.
 * This is an object containing status and message fields for order and payment.
 * Used to display feedback and status updates to the user throughout the checkout process.
 */
const orderMessages = computed(() => getMessages());

/**
 * Computed property for the current order status.
 * This is a string value that represents the current state of the order (e.g., processing, completed, failed).
 * Used to control UI flow and display the current state of the order.
 */
const orderStatus = computed(() => getOrderStatus());

/**
 * Computed property for the currently selected payment method.
 * Used to determine which payment logic and validations to apply (e.g., cash, PromptPay).
 */
const selectedMethod = computed(() => getPaymentMethod());

/**
 * Computed property for the original total amount of the order (before discounts).
 * Used as the base for all calculations, including discounts and change due.
 */
const totalAmount = computed(() => getTotalAmount());

/**
 * The number of seconds to wait before the transaction times out.
 * This is used to track the time remaining for the payment to be completed.
 */
const TIMER_COUNTER = 60;

/**
 * The size of the QR code.
 * This is used to display the QR code in the checkout modal.
 */
const QR_CODE_SIZE = 150;

/**
 * The new total amount after applying discounts.
 * This is used to calculate the due amount after discounts.
 *
 * We use a separate ref from totalAmount to preserve the original order total (before discounts).
 * This allows us to show both original and discounted totals, and to perform calculations (like change due)
 * based on the discounted value, without mutating or losing the original amount.
 */
const newTotalAmount = ref(totalAmount.value);

/**
 * The cash discount amount.
 * This is used to calculate the due amount after discounts.
 */
const cashDiscount = ref(0);

/**
 * The cash received amount.
 * This is used to calculate the change due.
 */
const cashReceived = ref(0);

/**
 * The change due amount.
 * This is used to calculate the change due after discounts.
 */
const change = ref(0);

/**
 * A flag indicating whether the transaction is currently processing.
 * This is used to show a loading state during payment processing.
 */
const isProcessing = ref(false);
const countdown = ref(TIMER_COUNTER);
const countdownInterval = ref(null);

/**
 * Error messages for the checkout process.
 * This is used to display error messages to the user.
 */
const errorMessages = ref({});

/**
 * The transaction status.
 * This is used to display the status of the transaction.
 */
const transactionStatus = ref(null);

/**
 * The transaction message.
 * This is used to display the message of the transaction.
 */
const transactionMessage = ref('');

const broadcastOrder = () => {
    broadcastOrderToCds();
}

/**
 * Rounds a number to two decimal places.
 * Uses Number.EPSILON to handle floating-point precision issues.
 * Returns the rounded number.
 * @param {number} num - The number to round.
 * @returns {number} The rounded number.
 *
 * Example:
 *   roundToTwo(1.005); // returns 1.01
 *   roundToTwo(2.499); // returns 2.5
 */
function roundToTwo(num) {
    return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Calculates the due amount after applying cash discount.
 * Rounds the cash received and discount values to two decimal places.
 * Validates that cash received and discount are non-negative.
 * Adjusts cash received and discount to zero if invalid.
 * Updates the cash discount and received values.
 * If the discount is exactly equal to the total amount, the order is considered free (due amount is zero).
 * Returns the due amount after discount.
 */
const calculateDue = debounce(() => {
    /**
     * Reset error messages.
     */
    errorMessages.value = {};

    /**
     * Round cash received and discount to two decimal places.
     */
    cashReceived.value = roundToTwo(Number(cashReceived.value) || 0);
    cashDiscount.value = roundToTwo(Number(cashDiscount.value) || 0);

    /**
     * Validate and adjust cash received and discount values.
     */
    if (!cashReceived.value || cashReceived.value < 0) {
        cashReceived.value = 0;
    }
    if (!cashDiscount.value || cashDiscount.value < 0) {
        cashDiscount.value = 0;
    }

    /**
     * Set the new total amount to the original total.
     * If the discount entered is greater than the total amount, show an error and exit early,
     * since a discount cannot exceed the order total.
     * If the discount is valid (greater than 0 and less than or equal to the total), subtract it from the total.
     * Note: A discount exactly equal to the total amount is allowed and will result in a free order (newTotalAmount = 0).
     *
     * Example:
     *   totalAmount = 100
     *   cashDiscount = 120   // Invalid, triggers error: "Discount cannot exceed total"
     *   cashDiscount = 100   // Valid, newTotalAmount = 100 - 100 = 0 (free order)
     *   cashDiscount = 20    // Valid, newTotalAmount = 100 - 20 = 80
     *   cashDiscount = 0     // No discount, newTotalAmount = 100
     */
    newTotalAmount.value = totalAmount.value;
    if (cashDiscount.value > totalAmount.value) {
        errorMessages.value['discount'] = t('discountExceedTotal');
        return;
    } else if (cashDiscount.value > 0 && cashDiscount.value <= totalAmount.value) {
        newTotalAmount.value = totalAmount.value - cashDiscount.value;
    }

    /**
     * For cash payments, calculate the change to give back to the customer.
     * - If the cash received is greater than or equal to the new total amount, calculate the change and clear any change-related error.
     * - If the cash received is less than the new total amount, set change to zero and show an error indicating insufficient cash.
     * - Always update the change value in the external state/store.
     *
     * Example:
     *   newTotalAmount = 80
     *   cashReceived = 100   // change = 20, no error
     *   cashReceived = 80    // change = 0, no error
     *   cashReceived = 50    // change = 0, error: "Insufficient cash, need 30 more"
     */
    if (selectedMethod.value === PAYMENT_METHOD_CASH) {
        if (cashReceived.value >= newTotalAmount.value) {
            change.value = roundToTwo(cashReceived.value - newTotalAmount.value);
            errorMessages.value['change'] = 0;
        } else {
            change.value = 0;
            errorMessages.value['change'] = t('insufficientCash', { amount: (newTotalAmount.value - cashReceived.value).toFixed(2) });
        }
        /**
         * Update the cash received value in store.
         */
        updateCashReceived(cashReceived.value);
        /**
         * Update the change value in store.
         */
        updateChange(roundToTwo(change.value));
    }

    /**
     * Update the discount value in store.
     */
    updateDiscount(DISCOUNT_TYPE_FIXED, cashDiscount.value);

    /**
     * Broadcast order to CDS
     */
    broadcastOrder();
}, 300);

/**
 * Computed property that generates a PromptPay URL for the current order.
 * Returns null if the order amount is invalid (non-positive).
 * Otherwise, returns a PromptPay URL with the order amount formatted to two decimal places.
 */
const promptPayValue = computed(() => {
    const amount = newTotalAmount.value;
    if (!amount || isNaN(amount) || amount <= 0) return null;
    return `PROMPTPAY:1234567890|AMOUNT:${amount.toFixed(2)}|CURRENCY:THB`;
});

/**
 * Computed property that determines whether the payment action should be enabled.
 * For cash payments, it checks if the order can be created and verifies that either
 * sufficient cash has been received or a discount fully covers the total amount due.
 * Returns true if payment is allowed, otherwise false.
 * 
 * @returns {boolean} - True if payment is allowed, otherwise false.
 */
const canMakePayment = computed(() => {
    /** Check if the selected payment method is cash. */
    if (selectedMethod.value === PAYMENT_METHOD_CASH) {
        /** Ensure that the user has permission to create the order. */
        if (!canCreateOrder.value) return false;
        /** 
         * This condition checks if the cash received is less than the amount due (after discount),
         * and also ensures that the discount does not fully cover the total amount.
         * If both are true, payment should not be allowed because the customer hasn't provided enough cash
         * and the discount isn't enough to make the due amount zero.
         *
         * Examples:
         *   totalAmount = 100
         *   cashDiscount = 10
         *   newTotalAmount = totalAmount - cashDiscount = 90
         *
         *   1. cashReceived = 50, cashDiscount = 10
         *      (50 < 90 && 10 != 90) => true
         *      // Not enough cash and discount doesn't cover the rest, payment not allowed.
         *
         *   2. cashReceived = 90, cashDiscount = 10
         *      (90 < 90 && 10 != 90) => false
         *      // Enough cash, payment allowed.
         *
         *   3. cashReceived = 0, cashDiscount = 100
         *      (0 < 0 && 100 != 0) => false
         *      // Discount covers total, payment allowed.
         *
         *   4. cashReceived = 0, cashDiscount = 90
         *      (0 < 10 && 90 != 10) => true
         *      // Not enough cash, discount doesn't cover total, payment not allowed.
         */
        if ((cashReceived.value < newTotalAmount.value && cashDiscount.value != newTotalAmount.value)) return false;
    }
    return true;
});

/**
 * Confirms the payment and processes the transaction.
 * If the payment is not allowed, it shows an error message.
 * Otherwise, it starts the payment processing and updates the transaction status.
 */
const confirmPayment = () => {
    /**
     * Checks if the payment is allowed based on the cash received and discount.
     * If the payment is not allowed, it shows an error message.
     * Otherwise, it starts the payment processing and updates the transaction status.
     */
    if (!canMakePayment.value) {
        errorMessages.value['process'] = t('insufficientCash', { amount: (newTotalAmount.value - cashReceived.value).toFixed(2) });
        return;
    }

    /**
     * Set the processing flag to true to indicate that payment is being processed.
     * This disables further user actions and can trigger loading indicators.
     */
    isProcessing.value = true;

    /**
     * Clear any previous error messages to ensure only relevant errors are shown for this payment attempt.
     */
    errorMessages.value = {};

    /**
     * If the selected payment method is PromptPay, start a countdown timer.
     * This is used to track the time remaining for the payment to be completed.
     */
    if (selectedMethod.value === PAYMENT_METHOD_PROMPTPAY) {
        countdown.value = TIMER_COUNTER;
        countdownInterval.value = setInterval(() => {
            countdown.value--;
            /**
             * If the countdown reaches zero, stop the timer.
             * If the transaction is still pending (i.e., no status has been set), 
             * mark the payment as not processing and show a timeout error message.
             * This prevents the UI from waiting forever and informs the user that the payment has expired.
             */
            if (countdown.value <= 0) {
                /**
                 * Stop the countdown timer to prevent it from running indefinitely and 
                 * avoid triggering the timeout logic multiple times.
                 */
                clearInterval(countdownInterval.value);
                if (!transactionStatus.value) {
                    /**
                     * Mark the payment as not processing and show a timeout error message.
                     */
                    isProcessing.value = false;
                    errorMessages.value['process'] = t('paymentTimeout');
                }
            }
        }, 1000);
    }

    // processTransaction(selectedMethod.value === PAYMENT_METHOD_PROMPTPAY ? 'success' : 'failed');
    processTransaction('success');
};

/**
 * Processes the transaction and updates the order status.
 * Returns the created order if successful, otherwise null.
 * @param {string} status - The status of the transaction ('success' or 'failed').
 * @returns {Promise<Object|null>} - The created order or null.
 */
const processTransaction = async (status) => {
    try {
        /**
         * Reset the processing flag to false to allow further user actions.
         */
        isProcessing.value = false;

        /**
         * Stop the countdown timer after the transaction is processed to prevent it 
         * from running in the background and causing unintended side effects.
         */
        clearInterval(countdownInterval.value);

        /**
         * If the transaction was successful, update the transaction status and message.
         */
        if (status === 'success') {
            /**
             * Update the transaction status and message.
             * Mark the transaction as completed and display a success message to the user.
             * This updates the UI to reflect that the payment was processed successfully.
             */
            transactionStatus.value = STATUS_COMPLETED;
            transactionMessage.value = t('paymentSuccessful');

            /**
             * If the payment method is cash and change is due, update the transaction message.
             */
            if (selectedMethod.value === PAYMENT_METHOD_CASH) {
                if (change.value > 0) {
                    transactionMessage.value = t('returnTheChange');
                }
            }

            /**
             * Update the transaction status and message.
             * Mark the transaction as completed and display a success message to the user.
             * This updates the UI to reflect that the payment was processed successfully.
             */
            updateTransactionStatus(STATUS_COMPLETED);
            updateMessages({
                "payment": {
                    "status": STATUS_COMPLETED,
                    "message": transactionMessage.value
                }
            });

            /**
             * Submit the order after successful payment.
             */
            handleSubmit();
        } else {
            throw new Error(t('transactionFailed'));
        }
    } catch (error) {
        /**
         * Handle errors that occur during transaction processing.
         * Set error messages and update the transaction status to failed.
         */
        transactionStatus.value = STATUS_FAILED;
        transactionMessage.value = t('paymentFailed');

        /**
         * Update the transaction status and message.
         * Mark the transaction as failed and display a failure message to the user.
         * This updates the UI to reflect that the payment was not processed successfully.
         */
        updateMessages({
            "payment": {
                "status": STATUS_FAILED,
                "message": transactionMessage.value
            }
        });
        console.error(t('paymentFailed'), error);
    } finally {
        /**
         * Reset the processing flag to false to allow further user actions.
         */
        isProcessing.value = false;
    }
};

/**
 * Resets the checkout modal state to initial values.
 * This includes clearing cash discounts, received cash, change, and other payment-related data.
 * Also resets transaction status and messages.
 */
const resetState = () => {
    cashDiscount.value = 0;
    cashReceived.value = 0;
    change.value = 0;
    newTotalAmount.value = totalAmount.value;
    isProcessing.value = false;
    countdown.value = TIMER_COUNTER;
    clearInterval(countdownInterval.value);
    errorMessages.value = {};
    transactionStatus.value = null;
    transactionMessage.value = '';
};

/**
 * Submits the order by creating it and updating the order status.
 * Returns the created order if successful, otherwise null.
 * @returns {Promise<Object|null>} - The created order or null.
 */
const submitOrder = async () => {
    const submissionData = getFormSubmissionData();
    try {
        /**
         * Create the order using the provided submission data.
         */
        const order = await createOrder(submissionData);
        if (order) {
            /**
             * Update the order status to processing.
             */
            updateOrderStatus(STATUS_PROCESSING);

            /**
             * Update the order messages to indicate that the order has been placed.
             */
            updateMessages({
                "order": {
                    "status": STATUS_COMPLETED,
                    "message": t('orderPlaced')
                }
            });

            /**
             * If the payment method is cash and change is due, update the payment messages.
             */
            if (selectedMethod.value === PAYMENT_METHOD_CASH && change.value > 0) {
                updateMessages({
                    "payment": {
                        "message": t('orderReturnChange')
                    }
                });
            }

            /**
             * Update the transaction status and message.
             * Mark the transaction as completed and display a success message to the user.
             * This updates the UI to reflect that the order was placed successfully.
             */
            transactionStatus.value = STATUS_COMPLETED;
            transactionMessage.value = t('orderPlaced');
            return order;
        }
        return null;
    } catch (error) {
        /**
         * Handle errors that occur during order submission.
         * Clear previous errors and set a new error message for the user.
         */
        errorMessages.value = {};
        errorMessages.value['process'] = t('orderFailed');

        /**
         * Update the order status and message.
         * Mark the order as failed and display a failure message to the user.
         * This updates the UI to reflect that the order was not placed successfully.
         */
        updateMessages({
            "order": {
                "status": STATUS_FAILED,
                "message": t('orderPlaceFailed')
            }
        });
        console.error(t('orderSubmitError'), error);
    }
};

/**
 * Prepares and returns the data object required for submitting an order.
 * This includes customer, table, payment, items, selected options, extras, and discounts.
 * The returned structure matches the expected payload for order submission.
 * 
 * @returns {Object} - An object containing the following properties:
 *   customer_name: string,
 *   order_type: string,
 *   table_id: number,
 *   payment_method: string,
 *   items: Array<{ product_id: number, quantity: number }>,
 *   addons_options: number[],
 *   discount: { order_id: number, discount_type: string, value: number }
 * }
 */
const getFormSubmissionData = () => {
    /**
     * Formats the order items into an array of objects containing product ID and quantity.
     * Also collects all selected option IDs and extra IDs from each order item into a single flat array for each item as "addons_options".
     * Example:
     *   Suppose getOrderItems() returns:
     *     [
     *       { food: { id: 1, ... }, quantity: 2 },
     *       { food: { id: 2, ... }, quantity: 1 }
     *     ]
     *   Then formattedItems will be: [{ product_id: 1, quantity: 2, addons_options: [] }, { product_id: 2, quantity: 1, addons_options: [1,3] }]
     */
    const formattedItems = getOrderItems().map((item) => {
        const optionsIds = Object.entries(item.options).map(([, opt]) => opt.id);
        const extrasIds = item.extras.map((extra) => extra.id);
        
        return {
            product_id: item.food.id,
            quantity: item.quantity,
            notes: item.notes,
            addons_options: [...optionsIds, ...extrasIds],
        };
    });

    /**
     * Retrieves the current table object.
     * @returns {Object} - An object of the current table.
     */
    const table = getCurrentTable();

    /**
     * Retrieves the current discount details.
     * @returns {Object} - An object containing discount details.
     */
    const discount = getDiscount();

    /**
     * Returns an object containing the following properties:
     *   customer_name: string - The name of the customer.
     *   order_type: string - The type of order (e.g., "dine-in", "take-away").
     *   table_id: number - The ID of the table where the order is placed.
     *   payment_method: string - The payment method used (e.g., "cash", "promptpay").
     *   items: Array<{ product_id: number, quantity: number }> - List of ordered items with their IDs and quantities.
     *   addons_options: number[] - List of selected option and extra IDs.
     *   discount: { order_id: number, discount_type: string, value: number } - Discount details.
     */
    return {
        customer_name: getCustomerName(),
        order_type: getOrderType(),
        table_id: table.id,
        payment_method: getPaymentMethod(),
        items: formattedItems,
        // addons_options: addonsOptions,
        discount: {
            order_id: 0,
            discount_type: discount.discountType,
            value: discount.value
        }
    };
};

/**
 * Submits the order by creating it and updating the order status.
 * Returns the created order if successful, otherwise null.
 * @returns {Promise<Object|null>} - The created order or null.
 */
const handleSubmit = async () => {
    /**
     * Submits the order by creating it and updating the order status.
     * Returns the created order if successful, otherwise null.
     */
    const order = await submitOrder();
    if (order && order.id) {
        /** 
         * Updates the order ID in the order store.
         * This id will be used in receipt.
         */
        updateOrderId(order.id);

        /**
         * Broadcast order to CDS
         */
        broadcastOrder();

        /* Reset payment reference id */
        setPaymentReferenceId();

        /**
         * Print the receipt automatically.
         * NOTE:
         * This was added later based on client requirement to download
         * the receipt for the order automatically and clear the cart.
         */
        setTimeout(() => {
            eventBus.emit('printReceipt');
            eventBus.emit('clearCart');
        }, 15);
    }
};

/**
 * Closes the checkout modal and resets the state.
 * If the order status is processing, it also resets the order store.
 */
const closeModal = () => {
    /**
     * Reset all payment-related fields and state.
     * This ensures that the payment section is reset when the modal is closed.
     */
    resetState();
    /**
     * Broadcast order to CDS.
     */
    broadcastOrder();
    /**
     * Closes the modal.
     * This ensures that the modal is closed when the payment section is reset.
     */
    emit('update:isOpen', false);
    /**
     * Resets the order store if the order status is processing.
     * This ensures that the order store is reset when the modal is closed.
     */
    if (orderStatus.value == STATUS_PROCESSING) {
        useOrderStore().$reset();
    }
};

/**
 * Watches for changes to the modal's open state.
 * If the modal is closed, it resets the state.
 */
watch(() => props.isOpen, (newVal) => {
    if (!newVal) resetState();
});

/**
 * Watch for changes to the total order amount.
 * When the total amount changes (e.g., items are added/removed or discounts change),
 * update the new total amount and recalculate all payment-related fields.
 * This ensures the payment section always reflects the current order state.
 */
watch(totalAmount, (newAmount) => {
    newTotalAmount.value = newAmount;
    calculateDue();
});

/**
 * Watch for changes to the selected payment method.
 * When the payment method changes, reset all payment-related fields and state.
 * This prevents data from the previous method (e.g., cash amounts, discounts, errors)
 * from incorrectly affecting the new payment flow.
 */
watch(selectedMethod, () => {
    resetState();
});

/**
 * When the component is unmounted, clear the countdown interval.
 */
onBeforeUnmount(() => {
    clearInterval(countdownInterval.value);
});
</script>

<style scoped>
/* Styles remain the same */

.small-modal {
    display: flex;
    justify-content: center;
    align-items: center;
}

.small {
    max-width: 400px;
    padding: 20px;
}

.total-amount {
    text-align: center;
    margin-bottom: 20px;
}

.payment-methods {
    display: flex;
    justify-content: space-around;
    margin-bottom: 20px;
}

.payment-methods button {
    padding: 10px 20px;
    border: 1px solid #ccc;
    border-radius: 5px;
    background: #f0f0f0;
    cursor: pointer;
    font-size: 1rem;
}

.payment-methods button.active-method {
    background: #007bff;
    color: white;
    border-color: #007bff;
}

.payment-section,
.order-messages-section {
    margin-bottom: 20px;
    text-align: center;
}

.order-messages-section {
    padding: 30px 0px;
}

.payment-instruction {
    padding: 15px;
}

.qr-code-placeholder {
    margin-top: 10px;
}

.qr-code-placeholder img {
    width: 150px;
    height: 150px;
}

.cash-input-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;
    font-weight: 700;
}

.cash-input {
    display: flex;
    /* Use flexbox for label and input layout */
    justify-content: space-between;
    /* Space between label and input */
    align-items: center;
    /* Vertically align items */
    width: 100%;
}

.cash-input label {
    text-align: left;
    width: 50%;
    /* Label takes 50% width */
}

.cash-input input {
    padding: 5px;
    border: 1px solid #ccc;
    border-radius: 5px;
    width: 50%;
    /* Input takes 50% width */
    text-align: center;
    /* Center the text inside the input */
}

.modal-actions {
    display: flex;
    justify-content: space-around;
    margin-top: 20px;
}

.total-to-pay {
    text-align: center;
}

.total-to-pay .label {
    font-size: 16px;
}

.total-to-pay .amount {
    font-size: 36px;
    font-weight: bold;
    color: #007bdf;
    /* --blue-500 */
}

.transaction-result {
    margin-top: 20px;
    text-align: center;
}

.success-message {
    display: block;
    color: green;
    font-weight: 500;
    padding: 0px 30px;
}

.error-message {
    color: red;
    font-weight: 500;
    display: block;
}

.order-success {
    padding: 0px 15px;
    margin-bottom: 15px;
}
</style>